import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Switch
} from '@mui/material';
import { ArrowLeft, Search, Plus, SquarePen, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getCenterCategories, createCenterCategory, updateCenterCategory, deleteCenterCategory } from '../../Services/ContentHub/SearchCentersApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const GetCenterCategories = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        isActive: true
    });
    const [submitting, setSubmitting] = useState(false);

    // Delete modal states
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch categories with search
    const fetchCategoriesData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getCenterCategories(debouncedSearchTerm);
            if (response && response.success) {
                setCategories(response.data || []);
            } else {
                setError("Failed to load categories.");
            }
        } catch (err) {
            setError("Failed to fetch center categories. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchTerm]);

    useEffect(() => {
        fetchCategoriesData();
    }, [fetchCategoriesData]);

    const handleOpenModal = (category = null) => {
        if (category) {
            setIsEdit(true);
            setCurrentCategory(category);
            setFormData({
                name: category.name || '',
                isActive: category.isActive !== undefined ? category.isActive : true
            });
        } else {
            setIsEdit(false);
            setCurrentCategory(null);
            setFormData({
                name: '',
                isActive: true
            });
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentCategory(null);
        setFormData({
            name: '',
            isActive: true
        });
        setError(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'name') {
            const sanitizedValue = value.replace(/[^a-zA-Z\s]/g, '');
            setFormData(prev => ({
                ...prev,
                name: sanitizedValue
            }));
            return;
        }
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.name.trim()) {
            setError("Category name is required.");
            return;
        } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
            setError("Category name cannot contain numbers or special characters.");
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            let res;
            if (isEdit) {
                res = await updateCenterCategory(currentCategory.id, {
                    ...formData,
                    name: formData.name.trim()
                });
            } else {
                res = await createCenterCategory({
                    ...formData,
                    name: formData.name.trim()
                });
            }
            showSnackbar(res?.message || `Category ${isEdit ? 'updated' : 'created'} successfully!`, "success");
            handleCloseModal();
            fetchCategoriesData();
        } catch (err) {
            const errMsg = err.response?.data?.message || err.message || `Failed to ${isEdit ? 'update' : 'create'} category`;
            setError(errMsg);
            showSnackbar(errMsg, "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedCategoryId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedCategoryId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const res = await deleteCenterCategory(selectedCategoryId);
                showSnackbar(res?.message || "Category deleted successfully!", "success");
                setDeleteModalOpen(false);
                setSelectedCategoryId(null);
                fetchCategoriesData();
            } catch (err) {
                const errMsg = err.response?.data?.message || err.message || "Failed to delete category";
                setDeleteError(errMsg);
                showSnackbar(errMsg, "error");
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate('/members/centers')} sx={{ color: '#444' }}>
                        <ArrowLeft size={24} />
                    </IconButton>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.5rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Center Categories
                    </Typography>
                </Stack>
                <Button onClick={() => handleOpenModal()}
                    disableElevation disableRipple disableFocusRipple disableTouchRipple
                    variant="contained" startIcon={<Plus size={18} />}
                    sx={{
                        bgcolor: '#FFBD65',

                        px: 2.5,
                        color: '#fff',
                        borderRadius: '6px',
                        height: '40px',
                        textTransform: 'none',
                        fontSize: '0.85rem'
                    }}
                >
                    Add Category
                </Button>
            </Box>

            {/* Search Section */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small" placeholder="Search by category name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        bgcolor: '#fff', width: { xs: "100%", md: "30%" },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            height: '40px',
                            fontSize: '0.85rem',
                            '& fieldset': { borderColor: '#E0E0E0' },
                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                <Search size={16} color="#999" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                <TableContainer>
                    <Table sx={{ minWidth: 950 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}> S.No</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Category Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : categories.length > 0 ? (
                                categories.map((cat, idx) => (
                                    <TableRow key={cat.id} sx={{
                                        transition: 'background-color 0.2s',
                                        '& td': { borderBottom: '1px solid #F0F0F0' },
                                        '&:last-child td': { borderBottom: 0 }
                                    }}>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>{cat.name}</TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                            <Box sx={{
                                                display: 'inline-block',
                                                px: 1.5, py: 0.3,
                                                borderRadius: '4px',
                                                bgcolor: cat.isActive ? '#E8F5E9' : '#FFEBEE',
                                                color: cat.isActive ? '#2E7D32' : '#D32F2F',
                                                fontSize: '0.7rem',
                                                fontWeight: 600
                                            }}>
                                                {cat.isActive ? 'Active' : 'Inactive'}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {cat.cuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {cat.uuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(cat.createdAt)}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(cat.updatedAt)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="Edit">
                                                    <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                        size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(cat)}>
                                                        <SquarePen size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                        size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(cat.id)}>
                                                        <Trash2 size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                        No categories found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Add/Edit Modal */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                    {isEdit ? 'Edit Category' : 'Add New Category'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <TextField size='small'
                            fullWidth
                            label="Category Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                }
                            }}
                        />
                        <FormControlLabel
                            control={<Switch checked={formData.isActive} onChange={handleInputChange} name="isActive"
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                }}
                            />}
                            label={<Typography sx={{ fontSize: '0.85rem' }}>Active</Typography>}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 1 }}>
                    <Button disableElevation disableFocusRipple disableRipple disableTouchRipple onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>
                        Cancel
                    </Button>
                    <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                        onClick={handleSubmit} disabled={submitting} variant="contained"
                        sx={{
                            bgcolor: '#FFBD65',

                            borderRadius: '6px',
                            textTransform: 'none',
                            px: 3,
                            color: '#fff',
                            fontSize: '0.85rem'
                        }}
                    >
                        {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <DeleteModal
                open={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setDeleteError(null);
                }}
                onDelete={handleConfirmDelete}
                title="Delete Category"
                description="Are you sure you want to delete this category? This action cannot be undone."
                loading={isDeleting}
                error={deleteError}
            />
        </Box>
    );
};

export default GetCenterCategories;
